import { useState } from "react";
import { Link } from "react-router-dom";
import { BET_LABEL, RISK_COLOR, SPORT_META, formatOdds } from "@/lib/picks";
import { Button } from "@/components/ui/button";
import { Lock, TrendingUp, ChevronRight, Sparkles, Eye, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import UpgradeModal from "@/components/UpgradeModal";
import EdgeExplainer, { deriveSignalGrade } from "@/components/EdgeExplainer";

interface Props {
  pick: any;
  sportCode: string;
  rank?: number;
  locked?: boolean;
  featured?: boolean;
}

const RESULT_META: Record<string, { label: string; color: string; Icon: any }> = {
  won: { label: "WON", color: "border-success/40 bg-success/15 text-success", Icon: CheckCircle2 },
  lost: { label: "LOST", color: "border-danger/40 bg-danger/15 text-danger", Icon: XCircle },
  pushed: { label: "PUSH", color: "border-muted-foreground/40 bg-muted/20 text-muted-foreground", Icon: MinusCircle },
};

export default function PickCard({ pick, sportCode, rank, locked, featured }: Props) {
  const meta = SPORT_META[sportCode] ?? SPORT_META.NBA;
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const settled = pick.result && pick.result !== "pending" ? RESULT_META[pick.result] : null;
  const unitsDelta = Number(pick.units_delta ?? 0);

  if (locked && settled) {
    const { Icon } = settled;
    return (
      <>
        <button
          type="button"
          onClick={() => setUpgradeOpen(true)}
          className="te-card relative overflow-hidden p-5 text-left w-full transition hover:shadow-elevated hover:-translate-y-0.5 hover:border-accent/40 duration-300 group"
        >
          <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-gradient-accent opacity-10 blur-3xl pointer-events-none" />
          {rank && (
            <div className="absolute -top-2 -left-2 w-12 h-12 grid place-items-center rounded-2xl bg-gradient-primary text-primary-foreground font-display font-bold text-lg shadow-glow z-10">
              #{rank}
            </div>
          )}
          <div className={`flex items-center justify-between gap-2 flex-wrap relative ${rank ? "ml-12" : ""}`}>
            <span className={`te-chip border ${meta.color}`}>{meta.icon} {meta.label}</span>
            <span className={`te-chip border font-bold tracking-wider ${settled.color}`}>
              <Icon className="w-3.5 h-3.5 mr-1 inline" /> {settled.label}
            </span>
          </div>

          <div className="mt-4 relative">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Matchup</div>
            <h3 className="font-display font-bold text-xl mt-1 leading-tight">
              {pick.teams || "Featured Matchup"}
            </h3>
          </div>

          <div className="grid grid-cols-3 gap-2.5 sm:gap-3 mt-5">
            <Stat label="Odds" value={formatOdds(pick.odds)} />
            <Stat label="Units" value={`${pick.unit_rating}u`} />
            <Stat
              label="Result"
              value={`${unitsDelta >= 0 ? "+" : ""}${unitsDelta.toFixed(2)}u`}
              tone={unitsDelta > 0 ? "success" : unitsDelta < 0 ? "danger" : undefined}
            />
          </div>

          <div className="mt-5 rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Lock className="w-3.5 h-3.5 text-accent shrink-0" />
              Premium reasoning & breakdown locked
            </div>
            <span className="te-chip bg-gradient-accent text-accent-foreground shadow-glow shrink-0">
              <Sparkles className="w-3 h-3 mr-1" /> Unlock today's AI analysis
            </span>
          </div>
        </button>
        <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
      </>
    );
  }

  if (locked) {
    const conf = Number(pick.confidence_score ?? 0);
    const risk = String(pick.risk ?? "med").toLowerCase();
    const riskLabel = risk === "low" ? "Low" : risk === "high" ? "High" : "Medium";
    const riskTone =
      risk === "low" ? "border-success/40 bg-success/10 text-success" :
      risk === "high" ? "border-danger/40 bg-danger/10 text-danger" :
      "border-warning/40 bg-warning/10 text-warning";
    const edgePct = conf > 50 ? Math.max(0, (conf - 50) / 10) : 0;
    const edgeLabel = edgePct > 0 ? `+${edgePct.toFixed(1)}% EV` : "—";
    const edgeStrength = conf >= 80 ? "Strong" : conf >= 65 ? "Moderate" : "Light";
    return (
      <>
        <button
          type="button"
          onClick={() => setUpgradeOpen(true)}
          className="te-card relative overflow-hidden p-5 text-left w-full transition hover:shadow-elevated hover:-translate-y-0.5 hover:border-accent/50 hover:ring-1 hover:ring-accent/30 duration-300 group"
          aria-label="Unlock full pick"
        >
          <div className="absolute -top-24 -right-24 w-52 h-52 rounded-full bg-gradient-accent opacity-[0.18] blur-3xl pointer-events-none transition-opacity duration-500 group-hover:opacity-30" />

          {rank && (
            <div className="absolute -top-2 -left-2 w-12 h-12 grid place-items-center rounded-2xl bg-gradient-primary text-primary-foreground font-display font-bold text-lg shadow-glow z-10">
              #{rank}
            </div>
          )}

          <div className={`flex items-center justify-between relative ${rank ? "ml-12" : ""}`}>
            <span className={`te-chip border ${meta.color}`}>{meta.icon} {meta.label}</span>
            <span className="te-chip border border-accent/40 bg-accent/10 text-accent">
              <Lock className="w-3 h-3 mr-1 inline" /> Pro Insight
            </span>
          </div>

          <div className="mt-4 relative">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{BET_LABEL[pick.bet_type] ?? "Matchup"}</div>
            <h3 className="font-display font-bold text-xl mt-1 leading-tight">
              {pick.teams || "Featured Matchup"}
            </h3>
          </div>

          {/* Teaser intelligence — no actual pick exposed */}
          <div className="mt-4 grid grid-cols-3 gap-2.5 sm:gap-3">
            <Stat label="Confidence" value={`${conf || "—"}`} accent />
            <Stat label="Edge" value={edgeLabel} accent />
            <Stat
              label="Risk"
              value={riskLabel}
              badge={riskTone}
            />
          </div>

          {/* Supporting signal labels — presentation only, no math change */}
          {(() => {
            const grade = deriveSignalGrade(conf, edgePct);
            return (
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className={`te-chip border text-[10px] tracking-wider font-bold ${grade.confidence.tone}`}>
                  {grade.confidence.label}
                </span>
                <span className={`te-chip border text-[10px] tracking-wider font-bold ${grade.marketSignal.tone}`}>
                  {grade.marketSignal.label}
                </span>
              </div>
            );
          })()}

          {conf > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
                <span>Model confidence</span><span className="tabular-nums font-semibold text-foreground">{conf}/100</span>
              </div>
              <div className="h-2 sm:h-1.5 rounded-full bg-secondary/80 ring-1 ring-border/50 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary via-primary to-accent shadow-[0_0_10px_hsl(var(--primary)/0.5)] animate-[confidence-fill_650ms_ease-out_both]"
                  style={{ width: `${Math.min(100, conf)}%` }}
                />
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
            <EdgeExplainer label="How is Edge calculated?" />
            <span className="text-[10px] text-muted-foreground/70 italic">
              Conservative · sportsbook-based
            </span>
          </div>

          <div className="mt-4 rounded-xl border border-accent/30 bg-accent/5 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-foreground/80 font-medium">Unlock full analysis</div>
              <span className="te-chip bg-gradient-accent text-accent-foreground shadow-glow shrink-0 group-hover:opacity-95">
                <Sparkles className="w-3 h-3 mr-1" /> Full AI breakdown
              </span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1.5">
              <Lock className="w-3 h-3 text-accent" /> Line, edge & reasoning locked
            </div>
          </div>
        </button>
        <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
      </>
    );
  }

  return (
    <div className={`te-card relative overflow-hidden p-4 sm:p-5 transition active:scale-[0.99] hover:shadow-elevated hover:-translate-y-0.5 duration-300 ${featured ? "border-primary/50 ring-1 ring-primary/20 shadow-elevated" : "hover:border-primary/40"}`}>
      {featured && (
        <div className="mb-3 inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
          <Sparkles className="h-3 w-3" /> Today's Free Pick
        </div>
      )}
      {rank && (
        <div className="absolute -top-2 -left-2 w-12 h-12 grid place-items-center rounded-2xl bg-gradient-primary text-primary-foreground font-display font-bold text-lg shadow-glow">
          #{rank}
        </div>
      )}

      <div className={`flex items-center justify-between flex-wrap gap-2 ${rank ? "ml-12" : ""}`}>
        <div className="flex gap-2 flex-wrap">
          <span className={`te-chip border ${meta.color}`}>{meta.icon} {meta.label}</span>
          <span className="te-chip border border-border bg-secondary text-secondary-foreground">
            {BET_LABEL[pick.bet_type]}
          </span>
        </div>
        {settled ? (
          <span className={`te-chip border font-bold tracking-wider ${settled.color}`}>
            <settled.Icon className="w-3.5 h-3.5 mr-1 inline" /> {settled.label}
          </span>
        ) : (
          <span className={`te-chip border ${RISK_COLOR[pick.risk]}`}>{pick.risk} risk</span>
        )}
      </div>

      <h3 className="font-display font-bold text-xl mt-4 leading-tight">{pick.title}</h3>
      {pick.teams && <p className="text-sm text-muted-foreground mt-1">{pick.teams}</p>}

      <div className="grid grid-cols-3 gap-2.5 sm:gap-3 mt-5">
        <Stat label="Odds" value={formatOdds(pick.odds)} />
        <Stat label="Units" value={`${pick.unit_rating}u`} accent={!settled} />
        {settled ? (
          <Stat
            label="Result"
            value={`${unitsDelta >= 0 ? "+" : ""}${unitsDelta.toFixed(2)}u`}
            tone={unitsDelta > 0 ? "success" : unitsDelta < 0 ? "danger" : undefined}
          />
        ) : (
          <Stat label="Confidence" value={`${pick.confidence_score}`} />
        )}
      </div>

      {!settled && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
            <span>Confidence</span><span className="tabular-nums font-semibold text-foreground">{pick.confidence_score}/100</span>
          </div>
          <div className="h-2.5 sm:h-2 rounded-full bg-secondary/80 ring-1 ring-border/50 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary via-primary to-accent shadow-[0_0_10px_hsl(var(--primary)/0.5)] animate-[confidence-fill_650ms_ease-out_both]"
              style={{ width: `${Math.min(100, pick.confidence_score)}%` }}
            />
          </div>
        </div>
      )}

      {pick.ai_summary && (
        <p className="mt-4 text-sm text-muted-foreground line-clamp-3">{pick.ai_summary}</p>
      )}

      <div className="mt-5 flex gap-2">
        <Link to={`/picks/${pick.id}`} className="flex-1">
          <Button variant="secondary" className="w-full">
            <Eye className="w-4 h-4 mr-1.5" /> View Breakdown <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      {pick.sportsbook && (
        <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
          <TrendingUp className="w-3 h-3" /> {pick.sportsbook}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, tone, badge, sub }: { label: string; value: string; accent?: boolean; tone?: "success" | "danger"; badge?: string; sub?: string }) {
  const toneClass = tone === "success" ? "text-success" : tone === "danger" ? "text-danger" : accent ? "te-grad-text" : "";
  return (
    <div className="rounded-xl bg-secondary/60 px-3 py-2.5 border border-border/50 flex flex-col justify-center">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none mb-1.5">{label}</div>
      {badge ? (
        <span className={`inline-flex items-center self-start rounded-md border px-1.5 py-0.5 text-xs font-display font-bold leading-none ${badge}`}>
          {value}
        </span>
      ) : (
        <div className={`font-display font-bold text-lg leading-none tabular-nums ${toneClass}`}>{value}</div>
      )}
      {sub && <div className="text-[10px] text-muted-foreground/80 mt-1 leading-none">{sub}</div>}
    </div>
  );
}

